import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import NylasType from "@app/enum/NylasType";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import settingsAnalytics from "@app/scene/settings/settingsAnaytics";
import {
  getNylasCalendarInfo,
  isInitializing,
  isNylasCreating,
  isNylasDeleting,
} from "@app/store/nylas";
import { createNylas, updateNylas } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import { connectToCalendarNylas } from "../../util/connectToNylas";
import SettingPage from "../common/SettingPage";

import styles from "./CalendarSync.module.scss";
import CreateEditPane from "./component/CreateEditPane";
import InfoField from "./component/InfoField";

interface Props {
  createNylas: typeof createNylas.request;
  isNylasProcessing: boolean;
  nylasInfo?: NylasInfo;
  updateNylas: typeof updateNylas.request;
}

const CalendarSync: React.FC<Props> = ({
  createNylas,
  isNylasProcessing,
  nylasInfo,
  updateNylas,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [createEditPaneShowing, showCreateEditPane, hideCreateEditPane] = useBoolean();

  const calendarSyncAnalytics = useExtendedAnalytics(["Calendar Sync"], settingsAnalytics);

  const handleSyncClick = useCallback(() => {
    calendarSyncAnalytics.clicked(["Sync"]);
    connectToCalendarNylas();
  }, [calendarSyncAnalytics]);

  useEffect(() => {
    const params = new Map(new URLSearchParams(window.location.search).entries());
    if (params.size > 0 && !isNylasProcessing) {
      const authCode = params.get("code");
      if (authCode) {
        if (nylasInfo) {
          updateNylas({
            callback: () => showCreateEditPane(),
            nylas: { ...nylasInfo, authCode, nylasCrmActivityTypes: [] },
          });
        } else {
          createNylas({
            callback: () => showCreateEditPane(),
            nylas: {
              authCode,
              syncOptions: { incoming: false, outgoing: true },
              type: NylasType.CALENDAR,
            },
          });
        }
        history.replace(`${Path.SETTINGS}/${SettingPath.PERSONAL_CALENDAR_SYNC}`);
      }
    }
  }, [createNylas, history, isNylasProcessing, nylasInfo, showCreateEditPane, updateNylas]);

  if (isNylasProcessing) {
    return <LoadingSpinner global />;
  }

  return (
    <SettingPage
      title={intl.formatMessage({
        id: "settings.calendar.title",
        defaultMessage: "Calendar Sync",
        description: "Title of settings calendar page",
      })}
    >
      <div className={styles.container}>
        <p className={styles.description}>
          {intl.formatMessage({
            id: "settings.calendar.description",
            defaultMessage:
              "Calendar Sync lets you automatically see Map My Customers activities in your calendar client, as well as see your calendar events as activities in Map My Customers.",
            description: "Description of settings calendar page",
          })}
        </p>
        {nylasInfo ? (
          <InfoField
            analyticIssuer={calendarSyncAnalytics}
            nylasInfo={nylasInfo}
            onShowPane={showCreateEditPane}
            onSync={handleSyncClick}
          />
        ) : (
          <Button onClick={handleSyncClick} type="primary">
            {intl.formatMessage({
              id: "settings.calendar.addSync",
              defaultMessage: "Sync with my calendar",
              description: "Settings calendar page - add sync",
            })}
          </Button>
        )}
      </div>
      {createEditPaneShowing && nylasInfo && (
        <CreateEditPane
          analyticIssuer={calendarSyncAnalytics}
          nylasInfo={nylasInfo}
          onHide={hideCreateEditPane}
        />
      )}
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  isNylasProcessing: isNylasCreating(state) || isNylasDeleting(state) || isInitializing(state),
  nylasInfo: getNylasCalendarInfo(state),
});

const mapDispatchToProps = {
  createNylas: createNylas.request,
  updateNylas: updateNylas.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarSync);
